import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import SwalAlertComp from "components/SwalAlert";
import { baseURL } from "configurations/environment";
import { getSorceApisAsync } from "helpers/leads/stepHelpers/sendStep1ToServis";
import { RequestConfig } from "helpers/RequestConfig";

import { GetLandingPagesParams } from "./landingPages.types";

export const getLandingPages = createAsyncThunk(
    "landingPages/getLandingPages",
    async (params: GetLandingPagesParams = {}) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append("page", params.page.toString());
        if (params.page_size)
            queryParams.append("page_size", params.page_size.toString());
        if (params.search) queryParams.append("search", params.search);

        const url = `${baseURL}/api/landing${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
        const response = await axios.get(url, RequestConfig());
        return {
            pages: response?.data?.data?.pages,
            pagination: response?.data?.data?.pagination,
        };
    },
);

export const getLandingPageTemplates = createAsyncThunk(
    "landingPages/getLandingPageTemplates",
    async () => {
        const response = await axios.get(
            `${baseURL}/api/landing/templates`,
            RequestConfig(),
        );
        return response?.data?.data?.templates;
    },
);

export const getIOSAppDetails = createAsyncThunk(
    "landingPages/getIOSAppDetails",
    async (appID: number) => {
        try {
            const response = await axios.get(
                `${baseURL}/api/landing/app_details?app_id=${appID}`,
                RequestConfig(),
            );
            SwalAlertComp({
                title: "Success",
                message:
                    "The page content has been successfully updated. You can now proceed to the next step.",
                icon: "success",
                timer: 3000,
            });
            return response?.data?.data;
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message: error?.response?.data.message,
                icon: "error",
            });
            return null;
        }
    },
);

export const getGenerateCode = createAsyncThunk(
    "landingPages/getGenerateCode",
    async (payload) => {
        const response = await axios.get(
            `${baseURL}/api/landing/generate-code`,
            RequestConfig(),
        );
        return response?.data?.data?.code;
    },
); // todo

export const getPublishLandingPage = createAsyncThunk(
    "landingPages/getPublishLandingPage",
    async (payload: any) => {
        try {
            const response = await axios.get(
                `${baseURL}/api/landing/${payload?.id}/publish `,
                RequestConfig(),
            );
            return response?.data?.data?.page;
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message: error?.response?.data?.message,
                icon: "error",
                timer: 3000,
            });
            throw error;
        }
    },
);

export const postCreateLandingPage = createAsyncThunk(
    "landingPages/postCreateLandingPage",
    async (payload: any) => {
        try {
            const response = await axios.post(
                `${baseURL}/api/landing`,
                payload,
                RequestConfig(),
            );

            return response?.data?.data?.page;
        } catch (error) {
            throw error?.response?.data;
        }
    },
);

export const getCloneLandingPage = createAsyncThunk(
    "landingPages/getCloneLandingPage",
    async (id: string, thunkAPI) => {
        const response = await axios.get(
            `${baseURL}/api/landing/${id}/clone`,
            RequestConfig(),
        );
        thunkAPI.dispatch(getLandingPages({}));

        return response.data;
    },
);

export const getMMPDetails = createAsyncThunk(
    "landingPages/getMMPDetails",
    async (trackerID: string) => {
        const response = await axios.get(
            `${baseURL}/api/trackers/${trackerID}/mmp-details`,
            RequestConfig(),
        );

        return response.data;
    },
);

export const getGamificationTemplates = createAsyncThunk(
    "landingPages/getGamificationTemplates",
    async () => {
        const response = await axios.get(
            `${baseURL}/api/landing/gamification/templates`,
            RequestConfig(),
        );

        return response?.data?.data;
    },
);

export const deleteLandingPage = createAsyncThunk(
    "landingPages/deleteLandingPage",
    async (id: number, thunkAPI) => {
        const response = await axios.delete(
            `${baseURL}/api/landing/${id}`,
            RequestConfig(),
        );
        thunkAPI.dispatch(getLandingPages({}));

        SwalAlertComp({
            title: "Success",
            message: "Landing page is deleted successfully.",
            icon: "success",
            timer: 3000,
        });
        return response?.data;
    },
);

export const putUpdateLandingPage = createAsyncThunk(
    "landingPages/putUpdateLandingPage",
    async (formData: any, thunkAPI) => {
        try {
            const id = formData.get("id");
            formData.delete("id");
            const response = await axios.post(
                `${baseURL}/api/landing/${id}/update`,
                formData,
                RequestConfig(),
            );

            thunkAPI.dispatch(getLandingPages({}));

            return response?.data;
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message: error?.response?.data?.message,
                icon: "error",
                timer: 3000,
            });
            throw error?.response?.data;
        }
    },
);

export const getLeadCustomSources = createAsyncThunk(
    "landingPages/getLeadCustomSources",
    async () => {
        const response = await getSorceApisAsync(
            null,
            `?filter["landing_page"]=true`,
        );

        return response?.data?.data;
    },
);
